import { initializeApp, FirebaseApp } from "firebase/app";
import { initializeAuth, Auth, browserLocalPersistence, indexedDBLocalPersistence, inMemoryPersistence, getAuth } from "firebase/auth";
import { getFirestore, Firestore, initializeFirestore } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getMessaging, Messaging } from "firebase/messaging";
import { getAnalytics, Analytics, isSupported } from "firebase/analytics";

const log = (msg: string) => {
    // console.log(`[FIREBASE-CONFIG] ${msg}`);
    // const box = document.getElementById('__boot_debug__');
    // if (box) {
    //     box.textContent += `\n[FB] ${msg}`;
    // }
};

// log('Module Evaluating...');

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// VALIDATION: Check if keys are missing
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    log('CRITICAL: Firebase Env Vars missing! Check your .env.local file.');
} else {
    const keyPrefix = typeof firebaseConfig.apiKey === 'string' ? firebaseConfig.apiKey.substring(0, 5) : 'MISSING';
    log(`Config Loaded: apiKey=${keyPrefix}... projectId=${firebaseConfig.projectId}`);
}

// Initialize Firebase
let app: FirebaseApp;
try {
    app = initializeApp(firebaseConfig);
    log('Firebase App Initialized: OK');
} catch (e: any) {
    log(`CRITICAL: Firebase App Init Failed: ${e.message}`);
    // Create a dummy app to prevent further crashes if possible, or rethrow
    throw e;
}

// 🔒 iOS FIX: Use IndexedDB as primary persistence so sessions survive
// WKWebView background kills. Falls back to localStorage, then in-memory.
// localStorage alone is NOT reliable on iOS — WKWebView purges it on background.
log('Initializing Auth (IndexedDB-first persistence)...');
let auth: Auth;
try {
    auth = initializeAuth(app, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence],
        popupRedirectResolver: undefined
    });
    log('Auth Initialized: OK (IndexedDB-first persistence)');
} catch (e: any) {
    log(`Auth Init Failed: ${e.message} -> Falling back to getAuth()`);
    auth = getAuth(app);
}

// NOTE: Do NOT manually init App Check for this project — Firebase Identity
// Platform auto-manages reCAPTCHA Enterprise keys for Phone Auth (visible in
// Firebase Console → Auth → SMS defense). A manually-initialised App Check
// with a separately-created key fights Firebase's own internal token retrieval
// and produces `auth/invalid-app-credential` + `appCheck/recaptcha-error`.

// 🔒 NETWORK FIX: Force Long Polling to bypass AdBlockers/Proxies
const db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
});
const storage = getStorage(app);

// 🔒 FCM: Messaging initialization (Browser Only)
let messaging: Messaging | undefined;
if (typeof window !== 'undefined') {
    try {
        messaging = getMessaging(app);
    } catch (e) {
        console.warn('[FIREBASE-CONFIG] Messaging not supported or failed to initialize:', e);
    }
}

// Analytics initialization (Optional, if supported)
let analytics: Analytics | undefined;
if (typeof window !== 'undefined') {
    isSupported().then(yes => {
        if (yes) analytics = getAnalytics(app);
    });
}

// 🔒 PATCH: No-op for compatibility
export const ensureFirebaseInitialized = async () => {
    log('Persistence check skipped (Nuclear Mode: Active)');
    return Promise.resolve();
};

export const getAuthSafe = async (): Promise<Auth> => {
    return auth;
};
export const getDbSafe = async (): Promise<Firestore> => {
    return db;
};
export const getStorageSafe = async (): Promise<FirebaseStorage> => {
    return storage;
};

export const getMessagingSafe = async (): Promise<Messaging | undefined> => {
    return messaging;
};

// Synchronous getters
export const getAuthIfReady = () => auth;
export const getDbIfReady = () => db;
export const getStorageIfReady = () => storage;
export const getMessagingIfReady = () => messaging;

export { auth, db, storage, messaging, analytics };
export default app;

log('Module Evaluated (REAL)');
